import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { logError, logDebug } from "@/lib/logger";
import { Toaster } from "@/components/ui/sonner";

// Pages
import AgeGate from "@/pages/AgeGate";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Gallery from "@/pages/Gallery";
import BookingEtiquette from "@/pages/BookingEtiquette";
import BookingInquiry from "@/pages/BookingInquiry";
import TourSchedule from "@/pages/TourSchedule";
import ExclusiveTravel from "@/pages/ExclusiveTravel";
import Gifts from "@/pages/Gifts";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Links from "@/pages/Links";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

// Components
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AvailabilityBanner from "@/components/AvailabilityBanner";
import { ContentProvider } from "@/lib/content";
import { LiveEditBar } from "@/components/live/LiveEditBar";
import { HelmetProvider } from "react-helmet-async";
import { SeoManager } from "@/components/Seo";

// Search/social crawlers see page content instead of the age gate so pages can be indexed.
const BOT_UA = /bot|crawl|spider|slurp|googlebot|bingbot|duckduckbot|yandex|baidu|facebookexternalhit|twitterbot|telegrambot|linkedinbot|whatsapp|discordbot|applebot|petalbot|ia_archiver/i;
const isCrawler = () => typeof navigator !== "undefined" && BOT_UA.test(navigator.userAgent);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Age Verification Context
const AgeContext = createContext();

export const useAge = () => useContext(AgeContext);

const AgeProvider = ({ children }) => {
  const [isVerified, setIsVerified] = useState(() => isCrawler());

  useEffect(() => {
    const verified = localStorage.getItem("age_verified");
    if (verified === "true") {
      setIsVerified(true);
    }
  }, []);

  const verify = () => {
    localStorage.setItem("age_verified", "true");
    setIsVerified(true);
  };

  return (
    <AgeContext.Provider value={{ isVerified, verify }}>
      {children}
    </AgeContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("admin_token"));

  const fetchAdmin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmin(response.data);
    } catch (error) {
      logError("Failed to fetch admin session, logging out:", error);
      logout();
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchAdmin();
    }
  }, [token, fetchAdmin]);

  const login = (accessToken, adminData) => {
    localStorage.setItem("admin_token", accessToken);
    setToken(accessToken);
    setAdmin(adminData);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setAdmin(null);
  };

  return (
    <AuthContext.Provider value={{ admin, token, login, logout, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Analytics tracker
const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/public/analytics/track`, {
          event_type: "pageview",
          page: location.pathname,
        });
      } catch (error) {
        // Analytics is non-critical; log for debugging without disrupting UX.
        logDebug("Analytics tracking failed:", error);
      }
    };
    trackPageView();
  }, [location]);
};

// Layout with navigation
const MainLayout = ({ children }) => {
  const { isVerified } = useAge();
  useAnalytics();

  if (!isVerified) {
    return <AgeGate />;
  }

  return (
    <div className="min-h-screen bg-ivory">
      <SeoManager />
      <div className="grain-overlay" />
      <AvailabilityBanner />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <LiveEditBar />
    </div>
  );
};

// Admin Layout
const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-ivory">
      {children}
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<MainLayout><Home /></MainLayout>} />
      <Route path="/about" element={<MainLayout><About /></MainLayout>} />
      <Route path="/gallery" element={<MainLayout><Gallery /></MainLayout>} />
      <Route path="/booking-etiquette" element={<MainLayout><BookingEtiquette /></MainLayout>} />
      <Route path="/booking" element={<MainLayout><BookingInquiry /></MainLayout>} />
      <Route path="/tour" element={<MainLayout><TourSchedule /></MainLayout>} />
      <Route path="/travel" element={<MainLayout><ExclusiveTravel /></MainLayout>} />
      <Route path="/gifts" element={<MainLayout><Gifts /></MainLayout>} />
      <Route path="/faq" element={<MainLayout><FAQ /></MainLayout>} />
      <Route path="/blog" element={<MainLayout><Blog /></MainLayout>} />
      <Route path="/blog/:slug" element={<MainLayout><BlogPost /></MainLayout>} />
      <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
      <Route path="/links" element={<MainLayout><Links /></MainLayout>} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLayout><AdminLogin /></AdminLayout>} />
      <Route path="/admin/*" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AgeProvider>
        <HelmetProvider>
          <AuthProvider>
            <ContentProvider>
              <AppRoutes />
              <Toaster position="bottom-right" richColors />
            </ContentProvider>
          </AuthProvider>
        </HelmetProvider>
      </AgeProvider>
    </BrowserRouter>
  );
}

export default App;
